// When a trip's weather email is due, and what the modal's defaults are.
//
// Shared by the React modal and the daemon Lambda so the two cannot disagree
// about what "7:00 AM, from a week before through a week after" means -- the
// same reason Email.mjs is shared by the item-email card and its daemon.
//
// THE CLOCK
//
// The send hour is a LOCAL hour in the subscription's own IANA zone, not an
// integer GMT hour. A send window routinely straddles a DST change -- a window
// running from a week before a March trip to a week after crosses
// spring-forward -- and a stored GMT hour drifts by an hour part-way through
// it. Storing the zone means the drift is computed each hour rather than baked
// in when the subscription was created.
//
// Every date here is a BARE calendar day (YYYY-MM-DD) on that zone's clock. The
// user picked days on a calendar; an instant would land a day early or late
// depending on who read it. `lastSentAt` is the one real instant on the model
// and is always `new Date().toISOString()` -- never Common.databaseDateTimeString,
// whose local-offset spelling does not sort lexicographically against a `Z` one.

import { utcDayIndex } from './TripGroups.mjs';

const MS_PER_DAY = 86400000;

// How far either side of the trip the emails run by default.
export const DEFAULT_LEAD_DAYS = 7;
export const DEFAULT_TRAIL_DAYS = 7;

// 7:00 AM: before you leave the dock, after the morning forecast issue.
export const DEFAULT_SEND_HOUR = 7;

// `Number(null)` and `Number('')` are both 0, and 0 is a valid send hour
// (midnight) -- so a row that simply has no hour would silently become a
// midnight send rather than falling back to the default.
export function sendHourOf(subscription) {
  const raw = subscription?.sendHourLocal;
  if (raw == null || raw === '') return DEFAULT_SEND_HOUR;
  const n = Number(raw);
  return Number.isFinite(n) ? ((n % 24) + 24) % 24 : DEFAULT_SEND_HOUR;
}

const ZONE_PART_FORMATTERS = new Map();

function zoneParts(date, timeZone) {
  if (!ZONE_PART_FORMATTERS.has(timeZone)) {
    let formatter = null;
    try {
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        hour12: false,
      });
    } catch {
      console.warn(`Unknown time zone ${timeZone}; using the host's clock.`);
    }
    ZONE_PART_FORMATTERS.set(timeZone, formatter);
  }
  const formatter = ZONE_PART_FORMATTERS.get(timeZone);
  if (!formatter) return null;
  const parts = Object.fromEntries(
    formatter.formatToParts(date).map((part) => [part.type, part.value])
  );
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    // Intl renders midnight as "24" in some locales under hour12:false.
    hour: Number(parts.hour) % 24,
  };
}

// 'YYYY-MM-DD' on `timeZone`'s clock (the host's when no zone is given).
export function localDateString(date, timeZone = null) {
  const when = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(when.getTime())) return null;
  const parts = timeZone ? zoneParts(when, timeZone) : null;
  if (parts) return dayStringFromParts(parts);
  return dayStringFromParts({
    year: when.getFullYear(),
    month: when.getMonth() + 1,
    day: when.getDate(),
  });
}

// 0-23 on `timeZone`'s clock.
export function localHour(date, timeZone = null) {
  const when = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(when.getTime())) return null;
  const parts = timeZone ? zoneParts(when, timeZone) : null;
  return parts ? parts.hour : when.getHours();
}

function pad(n) {
  return String(n).padStart(2, '0');
}

function dayStringFromParts({ year, month, day }) {
  return `${year}-${pad(month)}-${pad(day)}`;
}

// A bare 'YYYY-MM-DD' <-> the calendar-day axis TripCalendar.mjs defines, so a
// stored trip date and a send window land on the same numbers. Deliberately not
// `new Date(string)` arithmetic: adding days on an instant crosses a DST change
// wrong, which is the whole reason that axis exists.
export function dayIndexFromDateString(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? ''));
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) / MS_PER_DAY;
}

export function dateStringFromDayIndex(index) {
  if (!Number.isFinite(index)) return null;
  const date = new Date(index * MS_PER_DAY);
  return dayStringFromParts({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  });
}

// The modal's defaults: a week before the trip's first leg through a week after
// the last day at its final destination.
//
// `group.throughDate` rather than `endDate` (the last leg's own start), and
// rather than the module-private throughDateFor: a trip that ends with a
// four-day stay runs four days longer than its last leg's date.
export function defaultSendWindow(
  group,
  { leadDays = DEFAULT_LEAD_DAYS, trailDays = DEFAULT_TRAIL_DAYS } = {}
) {
  const startIndex = utcDayIndex(group?.startDate);
  const endIndex = utcDayIndex(group?.throughDate ?? group?.endDate ?? group?.startDate);
  if (startIndex == null || endIndex == null) return { startDate: null, stopDate: null };
  return {
    startDate: dateStringFromDayIndex(startIndex - leadDays),
    stopDate: dateStringFromDayIndex(endIndex + trailDays),
  };
}

// Is this subscription due to send right now?
//
// The hourly cron gives this an hour of granularity, exactly as the item
// digest has. `nextSendLocalDate` is what stops a second send in the same day
// once the hour has passed: without it every run for the rest of the day would
// see hour >= sendHour and send again.
//
// The window is inclusive at both ends -- the user picked the day the emails
// stop, and stopping the day before would be a surprise.
export function isSubscriptionDue(subscription, now = new Date()) {
  if (!subscription?.email) return false;

  const timeZone = subscription.timeZone || null;
  const today = localDateString(now, timeZone);
  if (!today) return false;

  const hour = localHour(now, timeZone);
  if (hour == null || hour < sendHourOf(subscription)) return false;

  if (subscription.startDate && today < subscription.startDate) return false;
  if (subscription.stopDate && today > subscription.stopDate) return false;

  // No next-send date yet means "has never sent" -- send on the first run
  // inside the window rather than waiting a day for a date to be written.
  if (subscription.nextSendLocalDate && today < subscription.nextSendLocalDate) return false;

  return true;
}

// The day after `now` on the subscription's clock. Written after a send, and
// the reason a second run in the same hour-of-day does not send twice.
export function nextSendLocalDateAfter(subscription, now = new Date()) {
  const today = localDateString(now, subscription?.timeZone || null);
  const index = dayIndexFromDateString(today);
  return index == null ? null : dateStringFromDayIndex(index + 1);
}

// A row past its stop date can never send again, so the daemon deletes it
// rather than scanning it forever. Distinct from "not due today".
export function isSubscriptionExpired(subscription, now = new Date()) {
  if (!subscription?.stopDate) return false;
  const today = localDateString(now, subscription.timeZone || null);
  return Boolean(today && today > subscription.stopDate);
}

// The coarse filter the daemon's scan uses. A per-row zone cannot be evaluated
// by a DynamoDB filter expression, so this only has to be a superset: a day
// either side of UTC today covers every zone on earth, and isSubscriptionDue
// makes the real decision per row.
export function dueScanCutoff(now = new Date()) {
  const index = dayIndexFromDateString(localDateString(now, 'UTC'));
  return index == null ? null : dateStringFromDayIndex(index + 1);
}

// "Sends daily at 7:00 AM from Aug 16 through Sep 6." -- the modal's summary
// line, here rather than in the component so the words and the rules that
// produce them stay together.
export function describeSchedule(subscription) {
  if (!subscription) return '';
  const parts = [`Sends daily at ${formatLocalHour(sendHourOf(subscription))}`];
  if (subscription.startDate && subscription.stopDate) {
    parts.push(`from ${describeDay(subscription.startDate)} through ${describeDay(subscription.stopDate)}`);
  } else if (subscription.startDate) {
    parts.push(`from ${describeDay(subscription.startDate)}`);
  } else if (subscription.stopDate) {
    parts.push(`through ${describeDay(subscription.stopDate)}`);
  }
  return `${parts.join(' ')}.`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// 'Aug 16, 2026'. Formatted from the STRING's own digits, never through a Date:
// `new Date('2026-08-16')` is UTC midnight and renders as the 15th anywhere
// west of Greenwich, which is the bug this whole module is arranged to avoid.
export function describeDay(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? ''));
  if (!match) return '';
  return `${MONTHS[Number(match[2]) - 1]} ${Number(match[3])}, ${match[1]}`;
}

// '7:00 AM'. The picker stores a plain local hour, so this is a direct
// rendering -- deliberately NOT EmailSubscriptionsCard's localHourLabel, which
// translates a stored GMT hour and would offset this picker by the viewer's
// own offset.
export function formatLocalHour(hour) {
  const n = ((Number(hour) % 24) + 24) % 24;
  const suffix = n < 12 ? 'AM' : 'PM';
  const display = n % 12 === 0 ? 12 : n % 12;
  return `${display}:00 ${suffix}`;
}

// The zone the browser is in, for a new subscription. Not derived from the
// trip's position: that needs a timezone-lookup table for a value that is right
// in the overwhelmingly common case of a local boater planning a local trip,
// and it is a stored column, so a picker can be added later with no migration.
export function detectTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}
